import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
    CpmButton,
    CpmModal,
    CpmModalBody,
    CpmModalFooter,
    CpmStatusChip,
} from '../../../../components/design-system';
import { approveQueueItem } from '../../../../services/productMasterService';
import { getApiErrorMessage } from '../../../../utils/api/ApiClient';
import { toast } from '../../../../utils/toast';
import { CPM_LICENCE_EXCISE_COPY } from '../../../../constants/centralProductMaster.constants';

interface ApproveDialogProps {
    open: boolean;
    level: 'brand' | 'product' | 'variant' | 'stateProduct';
    masterId: string;
    name: string;
    status: string;
    facts?: Array<{ label: string; value: string }>;
    reason?: string;
    pendingChildrenCount?: number;
    /** State product with no category — Approve is blocked until they assign. */
    categoryBlocked?: boolean;
    categoryBlockedText?: string;
    onAssignCategory?: () => void;
    onClose: () => void;
    onApproved: () => void;
}

export default function ApproveDialog({
    open,
    level,
    masterId,
    name,
    status,
    facts = [],
    reason = '',
    pendingChildrenCount = 0,
    categoryBlocked = false,
    categoryBlockedText = '',
    onAssignCategory,
    onClose,
    onApproved,
}: ApproveDialogProps) {
    const [submitting, setSubmitting] = useState(false);
    const [approveError, setApproveError] = useState<string | null>(null);

    useEffect(() => {
        if (open) setApproveError(null);
    }, [open, masterId]);

    const levelLabel =
        level === 'brand'
            ? 'brand'
            : level === 'product'
              ? 'product'
              : level === 'variant'
                ? 'variant'
                : 'state product';

    const reasonLabel = status === 'PENDING' ? 'Why it is pending' : 'Why it needs review';

    const kidsNote =
        pendingChildrenCount > 0
            ? `${pendingChildrenCount}${
                  level === 'brand'
                      ? pendingChildrenCount === 1
                          ? ' product under this brand is'
                          : ' products under this brand are'
                      : pendingChildrenCount === 1
                        ? ' variant under this product is'
                        : ' variants under this product are'
              } still awaiting review. Approving the ${levelLabel} here does not approve ${
                  pendingChildrenCount === 1 ? 'it' : 'them'
              } — each record still needs its own review.`
            : '';

    const confirm = async () => {
        if (categoryBlocked) return;
        try {
            setSubmitting(true);
            setApproveError(null);
            await approveQueueItem({ level, masterId });
            toast.success('Approved — now Active');
            onApproved();
            onClose();
        } catch (err) {
            setApproveError(getApiErrorMessage(err) || 'Approval failed');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <CpmModal open={open} onClose={onClose} maxWidth={640} zIndex={71}>
            <div className="flex items-start gap-[14px]" style={{ padding: '22px 24px 16px' }}>
                <span
                    className={[
                        'shrink-0 inline-flex items-center justify-center w-[38px] h-[38px] rounded-[10px]',
                        approveError
                            ? 'bg-[var(--cpm-shortfall-bg)] text-[var(--cpm-shortfall-text)]'
                            : 'bg-[var(--cpm-success-bg)] text-[var(--cpm-success-text-light)]',
                    ].join(' ')}
                >
                    {approveError ? (
                        <svg
                            width="19"
                            height="19"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.2"
                        >
                            <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3" />
                            <path d="M12 9v4M12 17h.01" strokeLinecap="round" />
                        </svg>
                    ) : (
                        <svg
                            width="19"
                            height="19"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.4"
                        >
                            <path
                                d="M20 6 9 17l-5-5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                    )}
                </span>
                <div className="flex-1 min-w-0 flex flex-col" style={{ gap: 6 }}>
                    <h2 className="m-0 !text-[19px] !font-extrabold !tracking-[-0.02em] !leading-[1.2] !text-[var(--cpm-brand-navy)]">
                        {approveError ? 'Approval failed' : `Approve this ${levelLabel}?`}
                    </h2>
                    <p className="m-0 !text-[13px] !font-semibold !leading-[1.5] !text-[var(--cpm-text-secondary)]">
                        {approveError
                            ? approveError
                            : 'Marks it Active in the master catalog straight away, without going through the Verification Queue.'}
                    </p>
                </div>
            </div>

            <CpmModalBody className="!pt-0">
                <div className="flex flex-col" style={{ gap: 10 }}>
                    <div
                        className="flex flex-col rounded-[12px] border border-[var(--cpm-border-default)] bg-[var(--cpm-surface-subtle)]"
                        style={{ gap: 8, padding: '14px 16px' }}
                    >
                        <div className="flex items-center gap-[10px] flex-wrap">
                            <span className="!text-[15px] !font-extrabold !tracking-[-0.015em] !leading-[1.3] !text-[var(--cpm-text-primary)]">
                                {name}
                            </span>
                            <CpmStatusChip status={status} />
                        </div>
                        {facts.length > 0 ? (
                            <div
                                className="grid gap-x-[14px] gap-y-[10px]"
                                style={{
                                    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                                }}
                            >
                                {facts.map((f) => (
                                    <div
                                        key={f.label}
                                        className="flex flex-col min-w-0"
                                        style={{ gap: 3 }}
                                    >
                                        <span
                                            className="uppercase text-[var(--cpm-text-tertiary)]"
                                            style={{
                                                fontSize: 10,
                                                fontWeight: 800,
                                                letterSpacing: '0.07em',
                                            }}
                                        >
                                            {f.label}
                                        </span>
                                        <span className="!text-[12.5px] !font-bold !leading-[1.35] !text-[var(--cpm-text-secondary)]">
                                            {f.value}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        ) : null}
                    </div>

                    {reason ? (
                        <div
                            className="flex flex-col rounded-[12px]"
                            style={{
                                gap: 5,
                                padding: '13px 15px',
                                background:
                                    status === 'PENDING'
                                        ? 'var(--cpm-info-bg)'
                                        : 'var(--cpm-warning-bg)',
                                border: `1px solid ${
                                    status === 'PENDING'
                                        ? 'var(--cpm-info-border)'
                                        : 'var(--cpm-warning-border)'
                                }`,
                                color:
                                    status === 'PENDING'
                                        ? 'var(--cpm-info-text)'
                                        : 'var(--cpm-warning-text)',
                            }}
                        >
                            <span
                                className="uppercase"
                                style={{
                                    fontSize: 10.5,
                                    fontWeight: 800,
                                    letterSpacing: '0.07em',
                                    opacity: 0.85,
                                }}
                            >
                                {reasonLabel}
                            </span>
                            <span
                                className="!text-[12.5px] !font-bold !leading-[1.5]"
                                style={{ textWrap: 'pretty' }}
                            >
                                {reason}
                            </span>
                        </div>
                    ) : null}

                    {categoryBlocked ? (
                        <div
                            className="flex rounded-[12px] bg-[var(--cpm-shortfall-bg)] border-[1.5px] border-[var(--cpm-shortfall-border)]"
                            style={{ gap: 11, padding: '13px 15px' }}
                        >
                            <span className="shrink-0 inline-flex text-[var(--cpm-shortfall-text)] pt-px">
                                <svg
                                    width="15"
                                    height="15"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2.2"
                                >
                                    <rect width="18" height="11" x="3" y="11" rx="2" />
                                    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                                </svg>
                            </span>
                            <div className="flex-1 min-w-0 flex flex-col" style={{ gap: 5 }}>
                                <span className="!text-[12.5px] !font-extrabold !leading-[1.5] !text-[var(--cpm-shortfall-text)]">
                                    {categoryBlockedText ||
                                        'Assign an excise category before approving this state product.'}
                                </span>
                                {onAssignCategory ? (
                                    <button
                                        type="button"
                                        onClick={onAssignCategory}
                                        className="self-start bg-transparent border-0 p-0 cursor-pointer"
                                        style={{
                                            fontSize: 12,
                                            fontWeight: 800,
                                            color: 'var(--cpm-shortfall-text)',
                                            borderBottom: '1px solid var(--cpm-shortfall-border)',
                                        }}
                                    >
                                        {CPM_LICENCE_EXCISE_COPY.APPROVE_BLOCKED_LINK}
                                    </button>
                                ) : null}
                            </div>
                        </div>
                    ) : null}

                    {kidsNote ? (
                        <div
                            className="flex rounded-[12px] bg-[var(--cpm-info-bg)] border border-[var(--cpm-info-border)]"
                            style={{ gap: 11, padding: '13px 15px' }}
                        >
                            <span className="shrink-0 inline-flex text-[var(--cpm-info-text)] pt-px">
                                <svg
                                    width="15"
                                    height="15"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2.2"
                                >
                                    <circle cx="12" cy="12" r="10" />
                                    <path d="M12 16v-4M12 8h.01" strokeLinecap="round" />
                                </svg>
                            </span>
                            <div className="flex-1 min-w-0 flex flex-col" style={{ gap: 5 }}>
                                <span className="!text-[12.5px] !font-bold !leading-[1.5] !text-[var(--cpm-info-text)]">
                                    {kidsNote}
                                </span>
                                <Link
                                    to="/central-product-master/verification-queue?tab=pending"
                                    className="self-start !text-[12px] !font-extrabold !text-[var(--cpm-info-text)] border-b border-[var(--cpm-info-text)]"
                                    onClick={onClose}
                                >
                                    Review those in the Verification Queue
                                </Link>
                            </div>
                        </div>
                    ) : null}
                </div>
            </CpmModalBody>

            <CpmModalFooter>
                <div className="ml-auto flex items-center gap-[10px]">
                    <CpmButton
                        variant="outline"
                        size="modal"
                        onClick={onClose}
                        disabled={submitting}
                    >
                        Cancel
                    </CpmButton>
                    <CpmButton
                        variant="success"
                        size="modal"
                        onClick={confirm}
                        isLoading={submitting}
                        disabled={categoryBlocked || submitting}
                        className={
                            categoryBlocked
                                ? '!opacity-100 !bg-[var(--cpm-surface-skeleton)] !text-[var(--cpm-text-tertiary)] !shadow-none border-[1.5px] !border-[var(--cpm-border-default)] hover:!brightness-100 hover:!translate-y-0'
                                : undefined
                        }
                        leadingIcon={
                            categoryBlocked || approveError ? undefined : (
                                <svg
                                    width="15"
                                    height="15"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2.6"
                                >
                                    <path
                                        d="M20 6 9 17l-5-5"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                            )
                        }
                    >
                        {submitting
                            ? 'Approving…'
                            : approveError
                              ? 'Try again'
                              : `Approve ${levelLabel}`}
                    </CpmButton>
                </div>
            </CpmModalFooter>
        </CpmModal>
    );
}
